import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { FaPlus } from "react-icons/fa6";
import { BsThreeDotsVertical } from "react-icons/bs";
import { LuFolderTree } from "react-icons/lu";
import FolderTreeNode from "./FolderTreeNode";

const FolderTreePanel = React.memo(
  ({
    selectedOrg,
    selectedFolder,
    onSelectFolder,
    renderedTree,
    treeLoading,
    expandedFolderIds,
    toggleFolderExpanded,
    handleExpandAll,
    handleCollapseAll,
    isSorted,
    setIsSorted,
    folderPermissions,
    user,
    draggingMoveItem,
    moveDropTargetId,
    onMoveDragStart,
    onMoveDragEnd,
    onMoveDragOverFolder,
    onMoveDropOnFolder,
    uploadsCountMap,
    onRenameFolder,
    onDeleteFolder,
    onManageMembers,
    orgStorageDetails,
    onAddFolder,
  }) => {
    const [showFolderOptions, setShowFolderOptions] = useState(false);

    // Close options dropdown on outside click (only when open)
    useEffect(() => {
      if (!showFolderOptions) return;
      const close = (e) => {
        if (!e.target.closest("button")) setShowFolderOptions(false);
      };
      document.addEventListener("click", close);
      return () => document.removeEventListener("click", close);
    }, [showFolderOptions]);

    return (
      <div className="w-[320px] flex-shrink-0 bg-white border border-slate-100 rounded-2xl shadow-sm flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <LuFolderTree size={16} className="text-indigo-500" />
            <h3 className="text-sm font-bold text-slate-800 tracking-wide">
              Folder Structure
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {folderPermissions.isEdit && (
              <button
                data-tooltip-id="toolbar-tooltip"
                data-tooltip-content="New Folder"
                onClick={onAddFolder}
                className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors"
              >
                <FaPlus size={12} />
              </button>
            )}
            <div className="relative">
              <button
                data-tooltip-id="toolbar-tooltip"
                data-tooltip-content="Action"
                onClick={() => setShowFolderOptions((v) => !v)}
                className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors"
              >
                <BsThreeDotsVertical size={12} />
              </button>
              {showFolderOptions && (
                <div className="absolute right-0 top-7 z-[90] w-44 bg-white border border-slate-100 rounded-xl shadow-xl py-1.5 animate-in fade-in duration-100">
                  {[
                    {
                      icon: (
                        <span className="text-[11px] font-bold">↕</span>
                      ),
                      label: "Expand All",
                      onClick: () => {
                        handleExpandAll();
                        setShowFolderOptions(false);
                      },
                    },
                    {
                      icon: (
                        <span className="text-[11px] font-bold">→</span>
                      ),
                      label: "Collapse All",
                      onClick: () => {
                        handleCollapseAll();
                        setShowFolderOptions(false);
                      },
                    },
                    {
                      icon: (
                        <span className="text-[10px] font-bold">A-Z</span>
                      ),
                      label: isSorted ? "Default Sorting" : "A→Z Sort Tree",
                      onClick: () => {
                        setIsSorted(!isSorted);
                        setShowFolderOptions(false);
                      },
                    },
                  ].map((a, i) => (
                    <button
                      key={i}
                      onClick={a.onClick}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors text-left font-medium"
                    >
                      {a.icon}
                      <span>{a.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Drag hint */}
        {folderPermissions.isEdit && selectedOrg && (
          <p className="px-4 py-1.5 text-[10px] font-medium text-slate-400 border-b border-slate-50">
            Drag items from the list and drop on a folder to move
          </p>
        )}

        {/* Tree body */}
        <div className="flex-1 overflow-y-auto py-2 px-2 scroll-inner">
          <div className="mb-1">
            {/* Org root drop zone */}
            {selectedOrg && folderPermissions.isEdit && (
              <div
                className={`mx-1 mb-2 px-2 py-2 rounded-lg text-[11px] font-semibold border border-dashed transition-colors ${moveDropTargetId === 0
                  ? "dns-move-drop-target border-indigo-400 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 text-slate-500 hover:border-indigo-200 hover:bg-slate-50"
                  }`}
                onDragOver={(e) => onMoveDragOverFolder(e, 0)}
                onDragLeave={() => {
                  /* parent resets moveDropTargetId via onMoveDragOverFolder */
                }}
                onDrop={(e) => onMoveDropOnFolder(e, 0)}
              >
                Drop here → organization root
              </div>
            )}

            {!selectedOrg ? (
              <div className="px-3 py-8 text-center">
                <LuFolderTree
                  size={22}
                  className="mx-auto text-slate-300 mb-2"
                />
                <p className="text-xs text-slate-400 font-medium">
                  Select an organization to view its folders
                </p>
              </div>
            ) : treeLoading ? (
              <div className="space-y-2 px-2 py-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="px-2 py-1.5">
                    <Skeleton width="80%" height={14} />
                  </div>
                ))}
              </div>
            ) : renderedTree.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <LuFolderTree
                  size={22}
                  className="mx-auto text-slate-300 mb-2"
                />
                <p className="text-xs text-slate-400 font-medium">
                  No folders yet
                </p>
              </div>
            ) : (
              renderedTree.map((node) => (
                <FolderTreeNode
                  key={node.folderDNSID ?? node.folderID}
                  node={node}
                  depth={1}
                  activeFolderId={
                    selectedFolder?.folderDNSID ?? selectedFolder?.folderID
                  }
                  onSelectFolder={onSelectFolder}
                  onRenameFolder={onRenameFolder}
                  onDeleteFolder={onDeleteFolder}
                  onManageMembers={
                    user?.parentID === 0 ? onManageMembers : undefined
                  }
                  expandedFolderIds={expandedFolderIds}
                  toggleFolderExpanded={toggleFolderExpanded}
                  isSorted={isSorted}
                  permissions={folderPermissions}
                  activeNodeUploadsMap={uploadsCountMap}
                  enableDragMove={folderPermissions.isEdit}
                  draggingMoveItem={draggingMoveItem}
                  moveDropTargetId={moveDropTargetId}
                  onMoveDragStart={onMoveDragStart}
                  onMoveDragEnd={onMoveDragEnd}
                  onMoveDragOverFolder={onMoveDragOverFolder}
                  onMoveDropOnFolder={onMoveDropOnFolder}
                />
              ))
            )}
          </div>
        </div>

        {/* Storage footer */}
        {selectedOrg && (
          <div className="bg-white border-t border-slate-100 p-2">
            <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4 flex justify-between">
              <h4 className="text-sm font-bold text-slate-800">
                Organization Storage
              </h4>
              <p className="text-xs font-semibold text-slate-500 mt-1">
                {(orgStorageDetails?.totalSize ?? 0).toFixed(1)} GB
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
);

FolderTreePanel.displayName = "FolderTreePanel";
export default FolderTreePanel;
